import React from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { tr } from "date-fns/locale";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TextField from "components/BufferedTextField";
import DecimalTextField from "components/DecimalTextField";
import { AddRowButton, RemoveRowButton } from "components/RowActions";
import { appChoose, appConfirm } from "components/AppDialog";
import {
    duplicateTruck,
    emptyTruck,
    normalizeCargoList,
    removeTruckFromCargo,
    setAllocation,
    splitCargoEvenly,
    totalBuyingRate,
    truckHasLoad,
    truckLabel,
    truckLoadTotals,
} from "./roadTruckModel";

const CURRENCIES = ["USD", "EUR", "GBP", "TRY"];

const LOAD_COLUMNS = [
    ["unitQuantity", "Units", 0],
    ["grossWeight", "Gross (kg)", 3],
    ["cbm", "CBM", 4],
    ["ldm", "LDM", 3],
    ["chargeableWeight", "Chargeable W.", 3],
];

const fmt = (value, decimals) =>
    (Number(value) || 0).toLocaleString("en-US", { maximumFractionDigits: decimals });

const cardSx = { mt: 2, p: 2, borderRadius: 2, border: "1px solid #e3e8ef" };

const DateTimeFields = ({ label, date, time, onDate, onTime, inputSx }) => (
    <>
        <Grid item xs={7} sm={4} md={2}>
            <DatePicker
                label={label}
                value={date}
                onChange={onDate}
                format="dd/MM/yyyy"
                slotProps={{
                    textField: { variant: "outlined", fullWidth: true, InputLabelProps: { shrink: true }, sx: inputSx },
                    actionBar: { actions: ["clear", "accept"] },
                }}
            />
        </Grid>
        <Grid item xs={5} sm={2} md={1}>
            <TextField
                variant="outlined"
                label="Time"
                type="time"
                value={time || ""}
                onChange={(e) => onTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 900 }}
                fullWidth
                sx={inputSx}
            />
        </Grid>
    </>
);

/**
 * "Trucks" section: one card per vehicle. On the road shipment form the trucks carry the
 * file's cargo lines (cargoDetails / setCargoDetails). On the RMBL page they are its linehaul
 * trucks and carry whole RHFLs instead: no cargo is passed, loadTotalsFor gives each truck's
 * load, assignedLabelFor names what it carries and onTruckRemoved moves that to another truck.
 */
export function TrucksSection({
    trucks,
    setTrucks,
    setCargoDetails,
    cargoDetails,
    truckTypeOptions,
    buyingRateCurrency,
    setBuyingRateCurrency,
    inputSx,
    title = "Trucks",
    loadTotalsFor,
    assignedLabelFor,
    onTruckRemoved,
}) {
    const withCargo = typeof setCargoDetails === "function";

    // The current cargo is brought in line with the old trucks first, so a one-truck
    // cargo's mirrored load is what the new truck list starts from.
    const applyTrucks = (next) => {
        if (withCargo) setCargoDetails((prev) => normalizeCargoList(normalizeCargoList(prev, trucks), next));
        setTrucks(next);
    };

    const updateTruck = (truckId, field, value) =>
        setTrucks((prev) => prev.map((t) => (t.truckId === truckId ? { ...t, [field]: value } : t)));

    const addTruck = () => applyTrucks([...trucks, emptyTruck({ truckType: trucks[0]?.truckType || "" })]);

    const copyTruck = (index) => {
        const next = [...trucks];
        next.splice(index + 1, 0, duplicateTruck(trucks[index]));
        applyTrucks(next);
    };

    const removeTruck = async (index) => {
        const truck = trucks[index];
        const remaining = trucks.filter((t) => t.truckId !== truck.truckId);
        let targetId = null;

        if (!withCargo) {
            const carried = assignedLabelFor?.(truck.truckId);
            if (carried) {
                const target = remaining[0];
                const ok = await appConfirm({
                    title: `Remove ${truckLabel(truck, index)}?`,
                    message: `${carried} will move from this truck to ${truckLabel(target, trucks.indexOf(target))}.`,
                    tone: "warning",
                    confirmText: "Remove truck",
                });
                if (!ok) return;
            }
            onTruckRemoved?.(truck.truckId, remaining[0]?.truckId || null);
            setTrucks(remaining);
            return;
        }

        if (truckHasLoad(cargoDetails, trucks, truck.truckId)) {
            const target = remaining[0];
            const choice = await appChoose({
                title: `Remove ${truckLabel(truck, index)}?`,
                message: "This truck carries cargo. Move its load to another truck or remove the load together with the truck.",
                tone: "warning",
                choices: [
                    { label: `Move load to ${truckLabel(target, trucks.indexOf(target))}`, value: "move", tone: "info" },
                    { label: "Remove with load", value: "drop", tone: "danger" },
                ],
            });
            if (!choice) return;
            if (choice === "move") targetId = target.truckId;
        }

        setCargoDetails((prev) => removeTruckFromCargo(prev, trucks, truck.truckId, targetId));
        setTrucks(remaining);
    };

    const rateTotal = totalBuyingRate(trucks);

    return (
        <MDBox mt={4}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <MDBox display="flex" alignItems="center" gap={1}>
                    <LocalShippingOutlinedIcon fontSize="small" />
                    <MDTypography variant="h6">{title}</MDTypography>
                    <Chip size="small" label={`${trucks.length} truck${trucks.length === 1 ? "" : "s"}`} />
                    <Chip
                        size="small"
                        variant="outlined"
                        sx={{ color: "#1A73E8", borderColor: "rgba(26,115,232,0.4)" }}
                        label={`Total buying rate: ${fmt(rateTotal, 2)} ${buyingRateCurrency || ""}`}
                    />
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1}>
                    <TextField
                        select
                        size="small"
                        label="Buying Rate Currency"
                        value={buyingRateCurrency || "EUR"}
                        onChange={(e) => setBuyingRateCurrency(e.target.value)}
                        sx={{ minWidth: 170 }}
                        InputLabelProps={{ shrink: true }}
                    >
                        {CURRENCIES.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </TextField>
                    <AddRowButton onClick={addTruck} label="Add Truck" />
                </MDBox>
            </MDBox>

            <LocalizationProvider dateAdapter={AdapterDateFns} locale={tr}>
                {trucks.map((truck, index) => {
                    const load = withCargo
                        ? truckLoadTotals(cargoDetails, trucks, truck.truckId)
                        : loadTotalsFor?.(truck.truckId) || {};
                    const carried = withCargo ? "" : assignedLabelFor?.(truck.truckId) || "";
                    const text = (field, label, props = {}) => (
                        <TextField
                            label={label}
                            value={truck[field] || ""}
                            onChange={(e) => updateTruck(truck.truckId, field, e.target.value)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={inputSx}
                            {...props}
                        />
                    );

                    return (
                        <MDBox key={truck.truckId} sx={cardSx} data-testid="road-truck-card">
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDTypography variant="button" fontWeight="bold">
                                        Truck {index + 1}
                                    </MDTypography>
                                    {truck.plate && <Chip size="small" label={truck.plate} />}
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => copyTruck(index)}
                                        sx={{ textTransform: "none", color: "#1A73E8 !important", borderColor: "rgba(26,115,232,0.4)" }}
                                    >
                                        Duplicate
                                    </Button>
                                    {trucks.length > 1 && (
                                        <RemoveRowButton onClick={() => removeTruck(index)} tooltip="Remove truck" />
                                    )}
                                </MDBox>
                            </MDBox>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4} md={3}>
                                    {text("truckType", "Truck Type", {
                                        select: true,
                                        children: [
                                            <MenuItem key="" value="">—</MenuItem>,
                                            ...truckTypeOptions.map((o) => (
                                                <MenuItem key={o.label} value={o.label}>{o.label}</MenuItem>
                                            )),
                                        ],
                                    })}
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("plate", "Plate Number", { placeholder: "34 ABC 123" })}</Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("trailerPlate", "Trailer Plate")}</Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("trailerType", "Trailer Type", { placeholder: "Tautliner, Frigo, Mega…" })}</Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("driverName", "Driver Name")}</Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("driverPhone", "Driver Phone")}</Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("carrier", "Carrier")}</Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <DecimalTextField
                                        label="Buying Rate"
                                        decimals={2}
                                        value={truck.buyingRate ?? ""}
                                        onChange={(e) => updateTruck(truck.truckId, "buyingRate", e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">{buyingRateCurrency || "EUR"}</InputAdornment>,
                                        }}
                                        sx={inputSx}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("cmrNumber", "CMR Number")}</Grid>
                                <Grid item xs={12} sm={4} md={3}>{text("sealNumber", "Seal Number")}</Grid>
                                <Grid item xs={12} sm={8} md={6}>{text("notes", "Notes")}</Grid>

                                <DateTimeFields
                                    label="Loading Date"
                                    date={truck.pickupDate}
                                    time={truck.pickupTime}
                                    onDate={(v) => updateTruck(truck.truckId, "pickupDate", v)}
                                    onTime={(v) => updateTruck(truck.truckId, "pickupTime", v)}
                                    inputSx={inputSx}
                                />
                                <DateTimeFields
                                    label="Estimated Delivery"
                                    date={truck.estimatedDeliveryDate}
                                    time={truck.estimatedDeliveryTime}
                                    onDate={(v) => updateTruck(truck.truckId, "estimatedDeliveryDate", v)}
                                    onTime={(v) => updateTruck(truck.truckId, "estimatedDeliveryTime", v)}
                                    inputSx={inputSx}
                                />
                                <DateTimeFields
                                    label="Actual Delivery"
                                    date={truck.actualDeliveryDate}
                                    time={truck.actualDeliveryTime}
                                    onDate={(v) => updateTruck(truck.truckId, "actualDeliveryDate", v)}
                                    onTime={(v) => updateTruck(truck.truckId, "actualDeliveryTime", v)}
                                    inputSx={inputSx}
                                />
                            </Grid>

                            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap" data-testid="road-truck-load">
                                <MDTypography variant="caption" fontWeight="bold" sx={{ alignSelf: "center", mr: 1 }}>
                                    Load on this truck:
                                </MDTypography>
                                {LOAD_COLUMNS.map(([field, label, decimals]) => (
                                    <Chip key={field} size="small" variant="outlined" label={`${label}: ${fmt(load[field], decimals)}`} />
                                ))}
                                {carried && (
                                    <Chip
                                        size="small"
                                        label={carried}
                                        sx={{ color: "#1A73E8", backgroundColor: "rgba(26,115,232,0.08)" }}
                                    />
                                )}
                            </MDBox>
                        </MDBox>
                    );
                })}
            </LocalizationProvider>
        </MDBox>
    );
}

/** Per-truck load of one cargo line; shown only when the file has more than one truck. */
export function CargoTruckLoads({ cargo, index, trucks, setCargoDetails }) {
    if (trucks.length < 2) return null;

    const current = normalizeCargoList([cargo], trucks)[0];
    const update = (truckId, field, value) =>
        setCargoDetails((prev) =>
            prev.map((c, i) => (i === index ? setAllocation(normalizeCargoList([c], trucks)[0], truckId, field, value, trucks) : c))
        );
    const split = () =>
        setCargoDetails((prev) =>
            prev.map((c, i) => (i === index ? splitCargoEvenly(normalizeCargoList([c], trucks)[0], trucks) : c))
        );

    return (
        <MDBox mt={2} p={1.5} borderRadius={2} sx={{ background: "rgba(26,115,232,0.04)", border: "1px dashed rgba(26,115,232,0.35)" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="caption" fontWeight="bold">
                    Load per truck (a total typed above is spread over the trucks; edit here to adjust)
                </MDTypography>
                <Button size="small" startIcon={<CallSplitIcon />} onClick={split} sx={{ textTransform: "none" }}>
                    Split evenly
                </Button>
            </MDBox>
            {trucks.map((truck, ti) => {
                const a = current.allocations.find((x) => x.truckId === truck.truckId) || {};
                return (
                    <Grid container spacing={1} key={truck.truckId} alignItems="center" mt={0}>
                        <Grid item xs={12} md={2}>
                            <MDTypography variant="button">{truckLabel(truck, ti)}</MDTypography>
                        </Grid>
                        {LOAD_COLUMNS.map(([field, label, decimals]) => (
                            <Grid item xs={6} sm={4} md={2} key={field}>
                                <DecimalTextField
                                    size="small"
                                    label={label}
                                    decimals={decimals}
                                    value={a[field] ?? 0}
                                    onChange={(e) => update(truck.truckId, field, e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                );
            })}
        </MDBox>
    );
}
